(function () {
  const API_URL = 'https://comfypet-mainfreight.onrender.com/quote';
  const LOCALITIES_URL = 'https://comfypet-mainfreight.onrender.com/localities';
  const MIN_AUTOCOMPLETE_CHARS = 3;
  const MAX_SUGGESTIONS = 8;

  let localitiesPromise = null;

  const getInitialCartItems = () => Array.isArray(window.mainfreightCartItems) ? window.mainfreightCartItems : [];

  const getInitialCartValue = () => {
    const value = Number(window.mainfreightCartValue || 0);
    return Number.isFinite(value) ? value : 0;
  };

  const getLiveCart = async () => {
    const response = await fetch('/cart.js', {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });

    if (!response.ok) {
      throw new Error('Failed to load live cart');
    }

    const cart = await response.json();
    const items = Array.isArray(cart.items)
      ? cart.items.map((item) => ({
          handle: item.handle,
          sku: item.sku,
          quantity: Number(item.quantity || 0),
        })).filter((item) => item.quantity > 0 && (item.handle || item.sku))
      : [];

    return {
      items,
      cartValue: Number(cart.total_price || 0) / 100,
    };
  };

  const escapeHtml = (value) => String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const normalizeText = (value) => String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const formatRate = (rate) => {
    const amount = Number(rate.price || 0);
    const value = Number.isFinite(amount) ? amount.toFixed(2) : '0.00';
    const name = rate.service_name || 'Shipping';
    const description = rate.description ? `<br><span style="font-size:0.92em;opacity:0.8;">${escapeHtml(rate.description)}</span>` : '';
    return `<strong>${escapeHtml(name)}</strong>: $${value} NZD${description}`;
  };

  const getLocalities = async () => {
    if (!localitiesPromise) {
      localitiesPromise = fetch(LOCALITIES_URL)
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to load localities');
          }
          return response.json();
        })
        .then((data) => Array.isArray(data.localities) ? data.localities : [])
        .catch((error) => {
          console.warn('Mainfreight localities unavailable', error);
          return [];
        });
    }

    return localitiesPromise;
  };

  const createSuggestionBox = (suburbInput) => {
    const box = document.createElement('div');
    box.className = 'mainfreight-locality-suggestions';
    box.hidden = true;
    box.style.marginTop = '8px';
    box.style.border = '1px solid rgba(0, 0, 0, 0.12)';
    box.style.borderRadius = '6px';
    box.style.background = '#fff';
    box.style.overflow = 'hidden';
    box.style.boxShadow = '0 6px 18px rgba(0, 0, 0, 0.08)';

    suburbInput.insertAdjacentElement('afterend', box);
    return box;
  };

  const hideSuggestions = (box) => {
    if (!box) {
      return;
    }
    box.hidden = true;
    box.innerHTML = '';
  };

  const showSuggestions = (box, matches, onSelect) => {
    if (!box || !matches.length) {
      hideSuggestions(box);
      return;
    }

    box.innerHTML = matches.map((entry, index) => `
      <button
        type="button"
        class="mainfreight-locality-option"
        data-mainfreight-locality-index="${index}"
        style="display:block;width:100%;padding:10px 12px;text-align:left;background:#fff;border:0;border-bottom:${index === matches.length - 1 ? '0' : '1px solid rgba(0,0,0,0.08)'};cursor:pointer;"
      >
        <strong>${escapeHtml(entry.suburb)}</strong><br>
        <span style="font-size:0.92em;opacity:0.8;">${escapeHtml(entry.city)} ${escapeHtml(entry.postcode)}</span>
      </button>
    `).join('');

    box.hidden = false;

    Array.from(box.querySelectorAll('[data-mainfreight-locality-index]')).forEach((button) => {
      button.addEventListener('mousedown', (event) => {
        event.preventDefault();
      });

      button.addEventListener('click', () => {
        const index = Number(button.getAttribute('data-mainfreight-locality-index'));
        const selected = matches[index];
        if (selected) {
          onSelect(selected);
        }
      });
    });
  };

  const getMatches = (localities, query) => {
    const term = normalizeText(query);
    if (!term || term.length < MIN_AUTOCOMPLETE_CHARS) {
      return [];
    }

    return localities
      .map((entry) => {
        const suburb = normalizeText(entry.suburb);
        const city = normalizeText(entry.city);
        let score = -1;

        if (suburb.startsWith(term)) {
          score = 0;
        } else if (suburb.includes(term)) {
          score = 1;
        } else if (city.startsWith(term)) {
          score = 2;
        } else if (city.includes(term)) {
          score = 3;
        }

        return score === -1 ? null : { entry, score };
      })
      .filter(Boolean)
      .sort((a, b) => {
        if (a.score !== b.score) {
          return a.score - b.score;
        }
        if (a.entry.suburb !== b.entry.suburb) {
          return a.entry.suburb.localeCompare(b.entry.suburb);
        }
        if (a.entry.city !== b.entry.city) {
          return a.entry.city.localeCompare(b.entry.city);
        }
        return a.entry.postcode.localeCompare(b.entry.postcode);
      })
      .slice(0, MAX_SUGGESTIONS)
      .map((item) => item.entry);
  };

  const findExactLocality = (localities, suburbValue, cityValue, postcodeValue) => {
    const suburb = normalizeText(suburbValue);
    const city = normalizeText(cityValue);
    const postcode = String(postcodeValue || '').trim();

    if (!suburb && !postcode) {
      return null;
    }

    const exactMatches = localities.filter((entry) => {
      if (postcode && entry.postcode !== postcode) {
        return false;
      }
      if (suburb && normalizeText(entry.suburb) !== suburb) {
        return false;
      }
      if (city && normalizeText(entry.city) !== city) {
        return false;
      }
      return true;
    });

    if (exactMatches.length === 1) {
      return exactMatches[0];
    }

    if (suburb) {
      const suburbOnlyMatches = localities.filter((entry) => normalizeText(entry.suburb) === suburb);
      if (suburbOnlyMatches.length === 1) {
        return suburbOnlyMatches[0];
      }
    }

    if (postcode) {
      const postcodeMatches = localities.filter((entry) => entry.postcode === postcode);
      if (postcodeMatches.length === 1) {
        return postcodeMatches[0];
      }
    }

    return null;
  };

  const applyLocality = (entry, suburbInput, cityInput, postcodeInput) => {
    if (!entry) {
      return;
    }

    if (suburbInput) {
      suburbInput.value = entry.suburb || '';
    }
    if (cityInput && !cityInput.value.trim()) {
      cityInput.value = entry.city || '';
    }
    if (postcodeInput && !postcodeInput.value.trim()) {
      postcodeInput.value = entry.postcode || '';
    }
  };

  const enableAutocomplete = async (suburbInput, cityInput, postcodeInput) => {
    if (!suburbInput || suburbInput.dataset.mainfreightAutocompleteBound === 'true') {
      return;
    }

    suburbInput.dataset.mainfreightAutocompleteBound = 'true';

    const localities = await getLocalities();
    if (!localities.length) {
      return;
    }

    const suggestionBox = createSuggestionBox(suburbInput);

    const refresh = () => {
      const matches = getMatches(localities, suburbInput.value || '');
      showSuggestions(suggestionBox, matches, (selected) => {
        suburbInput.value = selected.suburb || '';
        if (cityInput) {
          cityInput.value = selected.city || '';
        }
        if (postcodeInput) {
          postcodeInput.value = selected.postcode || '';
        }
        hideSuggestions(suggestionBox);
      });
    };

    suburbInput.addEventListener('input', () => {
      refresh();
    });

    suburbInput.addEventListener('focus', () => {
      if ((suburbInput.value || '').trim().length >= MIN_AUTOCOMPLETE_CHARS) {
        refresh();
      }
    });

    suburbInput.addEventListener('blur', () => {
      window.setTimeout(() => {
        hideSuggestions(suggestionBox);

        const exact = findExactLocality(
          localities,
          suburbInput.value,
          cityInput ? cityInput.value : '',
          postcodeInput ? postcodeInput.value : ''
        );
        if (exact) {
          applyLocality(exact, suburbInput, cityInput, postcodeInput);
        }
      }, 150);
    });

    if (postcodeInput) {
      postcodeInput.addEventListener('blur', () => {
        const exact = findExactLocality(
          localities,
          suburbInput ? suburbInput.value : '',
          cityInput ? cityInput.value : '',
          postcodeInput.value
        );
        if (exact) {
          applyLocality(exact, suburbInput, cityInput, postcodeInput);
        }
      });
    }
  };

  const bindCalculator = (calculator) => {
    if (!calculator || calculator.dataset.mainfreightBound === 'true') {
      return;
    }

    const suburbInput = calculator.querySelector('#mainfreight-suburb') || calculator.querySelector('[data-mainfreight-suburb]');
    const cityInput = calculator.querySelector('#mainfreight-city') || calculator.querySelector('[data-mainfreight-city]');
    const postcodeInput = calculator.querySelector('#mainfreight-postcode') || calculator.querySelector('[data-mainfreight-postcode]');
    const residentialInput = calculator.querySelector('#mainfreight-residential') || calculator.querySelector('[data-mainfreight-residential]');
    const button = calculator.querySelector('[data-mainfreight-submit]');
    const result = calculator.querySelector('[data-mainfreight-result]');

    if (!postcodeInput || !button || !result) {
      return;
    }

    calculator.dataset.mainfreightBound = 'true';
    enableAutocomplete(suburbInput, cityInput, postcodeInput);

    button.addEventListener('click', async () => {
      const postcode = (postcodeInput.value || '').trim();
      if (!postcode) {
        result.textContent = 'Please enter a postcode.';
        return;
      }

      result.textContent = 'Calculating…';

      try {
        const suburb = ((suburbInput && suburbInput.value) || '').trim();
        const city = ((cityInput && cityInput.value) || '').trim();
        let liveCart;
        try {
          liveCart = await getLiveCart();
        } catch (error) {
          liveCart = {
            items: getInitialCartItems(),
            cartValue: getInitialCartValue(),
          };
        }

        const payload = {
          destination: {
            suburb: suburb || city || 'New Zealand',
            city: city || suburb || 'New Zealand',
            postCode: postcode,
          },
          items: liveCart.items,
          cartValue: liveCart.cartValue,
          // Customer-facing cart estimates should include the residential
          // surcharge unless the theme explicitly presents an unchecked
          // residential control.
          isResidential: residentialInput ? !!residentialInput.checked : true,
        };

        if (!payload.items.length) {
          result.textContent = 'Your cart is empty.';
          return;
        }

        const response = await fetch(API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error('Request failed');
        }

        const data = await response.json();
        const rates = Array.isArray(data.rates) ? data.rates : [];

        if (rates.length) {
          result.innerHTML = rates.map(formatRate).join('<br>');
          return;
        }

        const charges = (data.response && data.response.charges) || [];
        const totalCharge = charges.find((charge) => charge.name === 'TotalIncludingGSTAmount');
        if (totalCharge) {
          const value = Number(totalCharge.value || 0);
          result.textContent = `Bulk freight: $${Number.isFinite(value) ? value.toFixed(2) : '0.00'} NZD`;
        } else {
          result.textContent = 'We couldn’t find a rate for that postcode. Please continue to checkout and we’ll confirm asap.';
        }
      } catch (error) {
        result.textContent = 'Could not fetch a shipping estimate. Please continue to checkout.';
      }
    });
  };

  const initCalculators = () => {
    document.querySelectorAll('[data-mainfreight-calculator]').forEach(bindCalculator);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCalculators);
  } else {
    initCalculators();
  }

  const observer = new MutationObserver(() => {
    initCalculators();
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
